import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authMe, authLogout, seedData } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewRole, setPreviewRole] = useState(null);

  // Effective user: preview role for platform admins, otherwise real user
  const effectiveUser = user && previewRole ? { ...user, role: previewRole, _realRole: user.role } : user;

  // Workspace helpers
  const isPlatformUser = user?.role === 'platform_owner' || user?.role === 'platform_admin' || user?.role === 'support_admin';
  const needsOnboarding = user && !isPlatformUser && !user.active_workspace_id && user.role === 'pending';
  const workspaceType = effectiveUser?.workspace_type;
  const workspaceId = effectiveUser?.workspace_id || effectiveUser?.active_workspace_id;
  const workspaceName = effectiveUser?.workspace_name;
  const workspaceStatus = effectiveUser?.workspace_status;

  // Role classification helpers
  const isWorkspaceOwner = ['company_owner', 'pharmacy_owner'].includes(effectiveUser?.role);
  const isWorkspaceAdmin = ['company_owner', 'company_admin', 'pharmacy_owner', 'pharmacy_admin'].includes(effectiveUser?.role);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authMe();
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip initial auth check if we're in the middle of an OAuth callback
    // (either hash-based or the new /auth/callback query-param-based flow)
    const isOAuthCallback = window.location.hash?.includes('session_id=') ||
      (window.location.pathname === '/auth/callback' && window.location.search?.includes('session_id='));
    if (isOAuthCallback) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) setLoading(false);
  }, [user]);

  const logout = async () => {
    try { await authLogout(); } catch {}
    setUser(null);
    setPreviewRole(null);
  };

  const seed = async () => {
    try { await seedData(); } catch {}
  };

  return (
    <AuthContext.Provider value={{
      user: effectiveUser, setUser, loading, logout, checkAuth, seed,
      previewRole, setPreviewRole, realUser: user,
      isPlatformUser, needsOnboarding,
      workspaceType, workspaceId, workspaceName, workspaceStatus,
      isWorkspaceOwner, isWorkspaceAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
